/**
 * 「停下这一轮」的幂等键：**同一次意图复用同一把，停成了就换一把**。
 *
 * 与 recovery-key.ts 同一条纪律，而这里两个方向都要紧，缺一个都出事：
 *
 * # 不复用会怎样
 *
 * 「提交成功但响应丢了」长得和「没提交成功」一模一样。用户看到一颗
 * 没反应的按钮，他会再点一次——带新钥匙的话服务端把它当成**第二次取消**，
 * 而这一轮已经全停了，于是它重新算一遍、得到 0，回一句
 * 「这一轮已经停了，没有还在跑的活」。他刚刚停掉三件，而界面说没有。
 *
 * # 不换一把会怎样（这一条是这个端点特有的）
 *
 * 停完之后他又开了一轮，再点「停下这一轮」——还拿着上一把钥匙的话，
 * 服务端认出重放，**一件活都不停**，还回上一轮那几个数字。
 * 屏幕上一切正常，而新那一轮照样在跑。
 *
 * 所以：成功之后立刻放掉；明确的 4xx 也放掉（这次意图不会成了）；
 * 网络错误与 5xx **不放**——那时我们不知道服务端收没收到，
 * 而不知道的时候换钥匙就是在赌。
 */

export { isDefinitiveRejection } from './recovery-key'

/**
 * 按会话发钥匙的小仓库。
 *
 * 做成一个对象而不是模块级的 Map，与 RecoveryKeys 同一个理由：
 * 模块级的那种会让两条用例互相看见对方的钥匙，而那种串扰表现成
 * 「第二条用例莫名其妙拿到了重放」。
 */
export class CancelRunKeys {
  private readonly issued = new Map<string, string>()

  /** 拿这条会话这一次叫停的钥匙。同一次意图**每次都是同一把**。 */
  keyFor(conversationId: string, mint: () => string = () => crypto.randomUUID()): string {
    const existing = this.issued.get(conversationId)
    if (existing !== undefined) return existing
    const fresh = mint()
    this.issued.set(conversationId, fresh)
    return fresh
  }

  /** 这次叫停结束了（成了，或者被明确拒了），放掉钥匙。 */
  release(conversationId: string): void {
    this.issued.delete(conversationId)
  }

  /** 这条会话现在有没有一把在用的钥匙。用例用。 */
  has(conversationId: string): boolean {
    return this.issued.has(conversationId)
  }
}
